import { ButtonContainer } from "./ButtonContainer";
import React, {
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { Stage, Layer } from "react-konva";
import URLImage from "./URLImage";
import Pitch from "./Pitch";
import Projection from "./Projection";
import useProjection from "./useProjection";
import { TModel } from "./models";
// import axios from 'axios';
import styled from "styled-components";
import {
  ThemeProvider,
  createMuiTheme,
  LinearProgress,
} from "@material-ui/core";
import PitchProjection from "./PitchProjection";

interface Props {
  model: TModel;
  defaultProjection?: boolean;
  previousCalibration?: TCalibration;
  previousPoints?: number[][];
  baseUrl?: string;
  cameraType?: string;
  image: HTMLImageElement;
  setDirty?: (dirty: boolean) => void;
  saveCalibration: (calibration: TCalibration, points?: number[][]) => void;
  readonly?: boolean;
}

export type TCalibration = {
  K: Array<Array<number>>;
  R: Array<Array<number>>;
  k: Array<Array<number>>;
  t: Array<Array<number>>;
  mean_square_error?: number;
};

const theme = createMuiTheme({
  palette: {
    primary: {
      main: "#00a06a",
    },
    secondary: {
      main: "#006D48",
      contrastText: "#ffffff",
    },
    action: {
      disabled: "rgba(255, 255, 255, 0.3)",
    },
  },
});
// const steps = 30;

function rotatePoint(
  x: number,
  y: number,
  centerx: number,
  centery: number,
  degrees: number
) {
  var newx =
    (x - centerx) * Math.cos((degrees * Math.PI) / 180) -
    (y - centery) * Math.sin((degrees * Math.PI) / 180) +
    centerx;
  var newy =
    (x - centerx) * Math.sin((degrees * Math.PI) / 180) +
    (y - centery) * Math.cos((degrees * Math.PI) / 180) +
    centery;
  return [newx, newy];
}

const inflateCoords = (coords: number[][], scale: number) => {
  return coords.map((coord) => [
    (coord[0] + coord[2]) * scale,
    (coord[1] + coord[2]) * scale,
  ]);
};

const determineCenter = (coords: number[][]) => {
  const minX = coords.map((p) => p[0]).sort((a, b) => a - b)[0];
  const minY = coords.map((p) => p[1]).sort((a, b) => a - b)[0];
  const maxX = coords.map((p) => p[0]).sort((a, b) => b - a)[0];
  const maxY = coords.map((p) => p[1]).sort((a, b) => b - a)[0];
  return [(maxX + minX) / 2, (maxY + minY) / 2];
};

//Because the template is in meters and 0,0 is top left, we scale it up 10 times
//so 1m = 10pixels and we rotate the pitch 180 degrees because most of the times
//the origin will be at the bottom right of the image
const rotateAndInflate = (
  coords: number[][],
  scale: number,
  rotate: number
) => {
  const inflated = inflateCoords(coords, scale);
  const center = determineCenter(inflated);

  return inflated.map((coord) =>
    rotatePoint(coord[0], coord[1], center[0], center[1], rotate)
  );
};

export const worldTemplateFromModel = (model: TModel) => {
  const origin = model.worldCoords[model.corners[0]];
  const topRight = model.worldCoords[model.corners[1]];

  let x = origin[0];
  let y = origin[1];
  const ret = [];

  while (x <= topRight[0]) {
    while (y <= topRight[1]) {
      ret.push([y, x, 0]);
      y += 1;
    }
    x += 1;
    y = 0;
  }
  return ret;
};

const Calibration: React.FunctionComponent<Props> = ({
  model,
  previousCalibration,
  previousPoints,
  baseUrl,
  cameraType,
  image,
  setDirty,
  saveCalibration,
  defaultProjection,
  readonly,
}) => {
  //A React ref to Konva Stage
  const stageRef = useRef<any>();
  //A React ref to the top level div
  const childRef = useRef<any>();
  const pitchRef = useRef<any>();
  //Width and height of the image on which we perform calibration
  const [resolution, setResolution] = useState<[number, number]>([
    image?.width,
    image?.height,
  ]);
  //Currently shown calibration candidate (index in list of candidates)
  const [candidateIndex, setCandidateIndex] = useState(0);
  //Scale level (zoom) of the canvas
  const [scale, setScale] = useState(0.5);
  //The position of the visible part of the Stage relative to the parent div
  const [position, setPosition] = useState({ x: 0, y: 0 });
  //Width of the parent div
  const [width, setWidth] = useState(0);
  const [calibrating, setCalibrating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [projecting, setProjecting] = useState(defaultProjection || false);
  const [pitchProjection, setPitchProjection] = useState(true);
  //Height of the parent div
  const [height, setHeight] = useState(0);
  const [points, setPoints] = useState<number[][]>(
    previousPoints || rotateAndInflate(model.worldCoords, 10, 180)
  );
  //A list of possible calibration candidates
  const [candidates, setCandidates] = useState<Array<TCalibration>>(
    previousCalibration ? [previousCalibration] : []
  );
  const [total, setTotal] = useState(0);
  const [advanced, setAdvanced] = useState(true);
  const [draggable, setDraggable] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [networkRequest, setNetworkRequest] = useState<
    XMLHttpRequest | undefined
  >();

  useEffect(() => {
    setResolution([image?.width, image?.height]);
  }, [image]);
  useEffect(() => {
    if (previousCalibration) {
      setCandidates([previousCalibration]);
    } else {
      setCandidates([]);
    }
    if (previousPoints) {
      setPoints(previousPoints);
    } else {
      setPoints(rotateAndInflate(model.worldCoords, 10, 180));
    }
  }, []);

  const stopCalibration = useCallback(() => {
    setCalibrating(false);
    if (networkRequest) {
      networkRequest.abort();
    }
  }, [setCalibrating, networkRequest]);

  const calibrate = useCallback(async () => {
    const data: {
      world_points: Array<Array<number>>;
      image_points: Array<Array<number>>;
      calibration: TCalibration | undefined;
      num_k: number;
      stadium: string;
      resolution: Array<number>;
    } = {
      world_points: [],
      image_points: [],
      num_k: 2,
      resolution: [],
      stadium: cameraType || "unknown",
      calibration: undefined,
    };

    let transformedPoints: Array<Array<number>> =
      pitchRef.current.getTransformedPoints();
    transformedPoints = transformedPoints.map((p) => [
      (p[0] - position.x) / scale,
      (p[1] - position.y) / scale,
    ]);
    setPoints(transformedPoints);

    const indices = transformedPoints
      .map((p, i) => {
        return { p: p, i: i };
      })
      .filter(
        (x) =>
          x.p[0] > 0 &&
          x.p[0] < resolution[0] &&
          x.p[1] > 0 &&
          x.p[1] < resolution[1]
      )
      .map((x) => x.i);
    data.world_points = model.worldCoords
      .filter((_p, i) => indices.includes(i))
      .map((p, i) => [i, p[1], p[0], 0]);
    data.image_points = transformedPoints
      .filter((_p, i) => indices.includes(i))
      .map((p, i) => [i, p[0], p[1]]);
    data.resolution = resolution;

    try {
      setCalibrating(true);
      const ret: Array<TCalibration> = [];
      let offset = 0;

      var xhr = new XMLHttpRequest();
      setNetworkRequest(xhr);
      xhr.open("POST", baseUrl + "/calibration", true);
      xhr.onprogress = function () {
        if (xhr.responseText.length > offset + 6) {
          if (offset === 0) {
            //The output starts with the number of expected responses
            const len = parseInt(
              xhr.responseText.substring(offset, offset + 6)
            );
            setTotal(len);
            offset = 6;
          } else {
            //Read two bytes of length
            while (xhr.responseText.length > offset + 6) {
              const len = parseInt(
                xhr.responseText.substring(offset, offset + 6)
              );
              if (xhr.responseText.length >= offset + 6 + len) {
                ret.push(
                  JSON.parse(
                    xhr.responseText.slice(offset + 6, offset + 6 + len)
                  )
                );
                offset += 6 + len;
              } else {
                break;
              }
            }
            setDirty && setDirty(true);
            setCandidates(
              [...ret].sort(
                (a, b) =>
                  (a.mean_square_error || 0) - (b.mean_square_error || 0)
              )
            );
          }
        }
      };

      xhr.onload = function () {
        while (xhr.responseText.length > offset + 6) {
          //Read two bytes of length
          const len = parseInt(xhr.responseText.substring(offset, offset + 6));
          if (xhr.responseText.length >= offset + 6 + len) {
            ret.push(
              JSON.parse(xhr.responseText.slice(offset + 6, offset + 6 + len))
            );
            offset += 6 + len;
          } else {
            break;
          }
        }
        setCandidates(
          [...ret].sort(
            (a, b) => (a.mean_square_error || 0) - (b.mean_square_error || 0)
          )
        );
        setCalibrating(false);
        setNetworkRequest(undefined);
      };

      xhr.setRequestHeader("content-type", "application/json");
      xhr.send(JSON.stringify(data));
    } catch (e) {
      console.log(e);
    }
  }, [setDirty, model, cameraType, baseUrl, resolution, position, scale]);

  const onSaveCalibration = useCallback(() => {
    if (candidates && candidateIndex < candidates.length) {
      saveCalibration(candidates[candidateIndex], points);
      setDirty && setDirty(false);
      setEditing(false);
      setCalibrating(false);
      if (networkRequest) {
        networkRequest.abort();
      }
    }
  }, [
    saveCalibration,
    setDirty,
    candidateIndex,
    candidates,
    points,
    networkRequest,
  ]);

  const deleteCandidate = useCallback(() => {
    if (candidates && candidateIndex < candidates.length) {
      candidates.splice(candidateIndex, 1);
      setCandidates([...candidates]);
    }
    if (candidates && candidateIndex >= candidates.length)
      setCandidateIndex(candidates.length - 1);
  }, [candidates, candidateIndex]);

  const worldTemplate = useMemo(() => {
    return worldTemplateFromModel(model);
  }, [model]);

  const { data: projectionData } = useProjection(
    worldTemplate,
    candidates && candidates[candidateIndex],
    baseUrl
  );

  React.useEffect(() => {
    //Determine the width and height of our parent div so we can make the konva stage the same
    function handleResize() {
      var style = window.getComputedStyle(childRef.current?.parentNode);
      const paddingHorizontal =
        parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const paddingVertical =
        parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      console.log(paddingHorizontal);
      console.log(childRef.current?.parentNode.clientWidth);
      setWidth(childRef.current?.parentNode.clientWidth - paddingHorizontal);
      setHeight(childRef.current?.parentNode.clientHeight - paddingVertical);
    }
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [childRef]);

  React.useEffect(() => {
    var scaleBy = 1.03;
    stageRef.current.on("wheel", (e: any) => {
      //The user has used the scrollWheel to zoom in/out
      e.evt.preventDefault();
      var oldScale = stageRef.current.scaleX();
      //The position of the mouse pointer relative to the top left of the visible part of the stage
      var mousePointTo = {
        x:
          stageRef.current.getPointerPosition().x / oldScale -
          stageRef.current.x() / oldScale,
        y:
          stageRef.current.getPointerPosition().y / oldScale -
          stageRef.current.y() / oldScale,
      };
      var newScale = e.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;
      stageRef.current.scale({ x: newScale, y: newScale });
      setScale(newScale);
      //Calculate the new top left position
      var newPos = {
        x:
          -(
            mousePointTo.x -
            stageRef.current.getPointerPosition().x / newScale
          ) * newScale,
        y:
          -(
            mousePointTo.y -
            stageRef.current.getPointerPosition().y / newScale
          ) * newScale,
      };
      //Position of the Stage relative to the parent
      setPosition(newPos);
      stageRef.current.position(newPos);
      stageRef.current.batchDraw();
    });
  }, []);

  const updatePoint = useCallback(
    (index, point) => {
      const update = [...points];
      update[index] = point;
      setDirty && setDirty(true);
      setPoints(update);
    },
    [points, setPoints, setDirty]
  );
  //Translate the points from the pitch back to coords in the shape

  const outOfBounds = useCallback(
    (x: number, y: number) => {
      const imgX = (x - position.x) / scale;
      const imgY = (y - position.y) / scale;
      return !(
        imgX >= 0 &&
        imgX <= resolution[0] &&
        imgY >= 0 &&
        imgY <= resolution[1]
      );
    },
    [position, scale, resolution]
  );

  const onDragEnd = useCallback((event: any) => {
    setPosition(event.target.getPosition());
  }, []);

  const redraw = useCallback(() => {
    stageRef.current.batchDraw();
  }, [stageRef]);

  const onToggleEdit = useCallback(() => {
    if (!readonly) {
      setEditing((curEditing) => {
        if (curEditing) {
          setDirty && setDirty(false);
          stopCalibration();
          if (previousCalibration) {
            setCandidates([previousCalibration]);
          } else {
            setCandidates([]);
          }
          setCandidateIndex(0);
        } else {
          setCandidates([]);
          setCandidateIndex(0);
          setCalibrating(false);
          if (networkRequest) {
            networkRequest.abort();
          }
        }
        return !curEditing;
      });
    }
  }, [
    setDirty,
    setEditing,
    setCalibrating,
    setCandidates,
    setCandidateIndex,
    networkRequest,
    stopCalibration,
    previousCalibration,
    readonly,
  ]);

  const onToggleProjection = useCallback(() => {
    setProjecting((curProjecting) => !curProjecting);
  }, [setProjecting]);

  const onTogglePitchProjection = useCallback(() => {
    setPitchProjection((curProjecting) => !curProjecting);
  }, [setPitchProjection]);

  return (
    <ThemeProvider theme={theme}>
      <Parent
        ref={childRef}
        onMouseMove={() => {
          if (!showButtons) {
            setShowButtons(true);
          }
        }}
        onMouseEnter={() => {
          setShowButtons(true);
        }}
        onMouseLeave={() => {
          setShowButtons(false);
        }}
      >
        <Container calibrating={calibrating ? 1 : 0}>
          <Stage
            ref={stageRef}
            width={width}
            height={height}
            draggable
            onDragEnd={onDragEnd}
            scaleX={scale}
            scaleY={scale}
          >
            <Layer>
              <URLImage image={image} setResolution={setResolution} />
              {editing && candidates.length === 0 && !calibrating ? (
                <Pitch
                  ref={pitchRef}
                  scale={scale}
                  position={position}
                  model={model}
                  updatePoint={updatePoint}
                  updatePoints={setPoints}
                  outOfBounds={outOfBounds}
                  redraw={redraw}
                  points={points || []}
                  draggable={draggable}
                  baseUrl={baseUrl || "https://calibration.balljames.com"}
                />
              ) : (
                ((!editing && projecting) ||
                  (editing && candidates.length > 0)) && (
                  <Projection
                    scale={scale}
                    template={worldTemplate}
                    projection={projectionData}
                  />
                )
              )}
              {((!editing && pitchProjection) ||
                (editing && candidates.length > 0)) && (
                <PitchProjection
                  resolution={resolution}
                  baseUrl={baseUrl}
                  calibration={candidates && candidates[candidateIndex]}
                  model={model}
                  scale={scale}
                />
              )}
            </Layer>
          </Stage>
        </Container>
        {calibrating && (
          <ProgressDiv>
            <LinearProgress
              variant="determinate"
              value={((candidates?.length || 0) * 100) / total}
            />
          </ProgressDiv>
        )}
        {(true || showButtons) && (
          <ButtonContainer
            projecting={projecting}
            toggleProjection={onToggleProjection}
            pitchProjection={pitchProjection}
            togglePitchProjection={onTogglePitchProjection}
            editing={editing}
            toggleEdit={onToggleEdit}
            calibrate={editing && !calibrating ? calibrate : undefined}
            candidates={candidates}
            saveCalibration={onSaveCalibration}
            advanced={advanced}
            deleteCandidate={deleteCandidate}
            stopCalibration={calibrating ? stopCalibration : undefined}
            candidateIndex={candidateIndex}
            image={image}
            model={model}
            previousCalibration={previousCalibration}
            setCandidateIndex={setCandidateIndex}
            setAdvanced={setAdvanced}
            setCandidates={setCandidates}
            setDraggable={setDraggable}
            readonly={readonly}
          />
        )}
      </Parent>
    </ThemeProvider>
  );
};

Calibration.defaultProps = {
  baseUrl: "https://calibration.balljames.com",
  cameraType: "unknown",
};

const Parent = styled.div`
  height: 100%;
  position: relative;
`;

const ProgressDiv = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  background-color: black;
  opacity: 0.5;
  z-index: 1;
`;

type ContainerProps = {
  calibrating: number;
};

const Container = styled.div<ContainerProps>`
  opacity: ${(props) => (props.calibrating === 1 ? 0.5 : 1)};
`;

export default Calibration;
